import { Component, OnInit } from "@angular/core";
import { Location } from "@angular/common";
import { ActivatedRoute } from "@angular/router";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import Swal from "sweetalert2";
import { catchError, of } from "rxjs";

import { Utils } from "../../../../../shared/utils/Utils";
import {
  CierresDesplazamientos,
  CierresDesplazamientosPayload,
  CierresDesplazamientosService,
} from "../../../../../shared/services/cierres-desplazamientos.service";
import { PesvDesplazamientosService } from "../../../../../shared/services/pesvDesplazamientos.service";

@Component({
  selector: "app-cierre-desplazamientos",
  templateUrl: "./cierre-desplazamientos.component.html",
  styleUrls: ["./cierre-desplazamientos.component.scss"],
})
export class CierreDesplazamientosComponent implements OnInit {
  // ============ TABS PRINCIPALES (formulario / listado) ============
  activeTab = 1;

  // Desplazamiento padre
  idDesplazamiento!: number;
  desplazamiento: any = null;

  get desplazamientoActivo(): boolean {
    return Number(this.desplazamiento?.estado) === 1;
  }

  cierreForm!: FormGroup;
  guardando = false;
  modoEdicion = false;
  idCierreEditando: number | null = null;

  // Listado
  cierres: CierresDesplazamientos[] = [];
  cierresFiltrados: CierresDesplazamientos[] = [];
  cargandoListado = false;
  filtro = "";
  filtroEstado: number | null = null;
  page = 1;
  pageSize = 10;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private location: Location,
    private cierresService: CierresDesplazamientosService,
    private desplazamientosService: PesvDesplazamientosService,
  ) {}

  ngOnInit(): void {
    this.idDesplazamiento = Number(this.route.snapshot.paramMap.get("id"));

    if (!this.idDesplazamiento) {
      Swal.fire("Error", "No se recibió un desplazamiento válido.", "error").then(() =>
        this.volver(),
      );
      return;
    }

    this.inicializarFormulario();
    this.cargarDesplazamiento();
    this.cargarCierres();
  }

  private inicializarFormulario(): void {
    this.cierreForm = this.fb.group({
      fecha_cierre: ["", Validators.required],
      hora_llegada_real: [""],
      kilometraje_final: [null, [Validators.min(0)]],
      cumplimiento: [null, [Validators.min(0), Validators.max(100)]],
      observaciones: [""],
    });
  }

  // ============ Desplazamiento padre ============
  private cargarDesplazamiento(): void {
    this.desplazamientosService
      .getByIdRiesgo(this.idDesplazamiento)
      .pipe(catchError(() => of(null)))
      .subscribe((respuesta: any) => {
        const data = respuesta?.data ?? respuesta;
        this.desplazamiento = Array.isArray(data) ? data[0] ?? null : data ?? null;
        this.activeTab = this.desplazamientoActivo ? 1 : 2;
      });
  }

  private normalizarLista(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    return [];
  }

  // ============ Helpers de presentación ============
  claseCumplimiento(valor?: number | null): string {
    if (valor === null || valor === undefined) return "bg-secondary";
    const v = Number(valor);
    if (v >= 80) return "bg-success";
    if (v >= 50) return "bg-warning text-dark";
    return "bg-danger";
  }

  // ============ Listado ============
  cargarCierres(): void {
    this.cargandoListado = true;
    // El servicio no tiene getByDesplazamiento: se trae todo y se filtra por desplazamiento
    this.cierresService.getAll().subscribe({
      next: (data: any) => {
        this.cierres = this.normalizarLista(data).filter(
          (c: any) => Number(c.desplazamiento_id) === this.idDesplazamiento,
        );
        this.aplicarFiltro();
        this.cargandoListado = false;
      },
      error: () => {
        this.cierres = [];
        this.cierresFiltrados = [];
        this.cargandoListado = false;
      },
    });
  }

  aplicarFiltro(): void {
    const termino = this.filtro.toLowerCase().trim();
    this.cierresFiltrados = this.cierres.filter((c) => {
      const coincideTexto =
        !termino ||
        [c.observaciones, c.fecha_cierre, c.cumplimiento, c.kilometraje_final]
          .filter((v) => v !== null && v !== undefined && v !== "")
          .some((v) => String(v).toLowerCase().includes(termino));

      const coincideEstado = this.filtroEstado === null || c.estado_cierre === this.filtroEstado;
      return coincideTexto && coincideEstado;
    });
    this.page = 1;
  }

  // ============ Guardar / editar ============
  private aNumeroONull(valor: any): number | null {
    return valor === null || valor === undefined || valor === "" ? null : Number(valor);
  }

  private aTextoONull(valor: any): string | null {
    if (valor === null || valor === undefined) return null;
    const t = String(valor).trim();
    return t === "" ? null : t;
  }

  private construirPayload(): CierresDesplazamientosPayload {
    const raw = this.cierreForm.getRawValue();
    return {
      desplazamiento_id: this.idDesplazamiento,
      fecha_cierre: raw.fecha_cierre,
      hora_llegada_real: this.aTextoONull(raw.hora_llegada_real),
      kilometraje_final: this.aNumeroONull(raw.kilometraje_final),
      cumplimiento: this.aNumeroONull(raw.cumplimiento),
      observaciones: this.aTextoONull(raw.observaciones),
    };
  }

  guardar(): void {
    if (!this.modoEdicion && !this.desplazamientoActivo) {
      Swal.fire("Desplazamiento inactivo", "No se pueden crear cierres para un desplazamiento inactivo.", "warning");
      return;
    }

    if (this.cierreForm.invalid) {
      this.cierreForm.markAllAsTouched();
      Swal.fire("Campos obligatorios", "Completa todos los campos requeridos.", "warning");
      return;
    }

    // Un desplazamiento solo debería tener un cierre activo
    if (!this.modoEdicion && this.cierres.some((c) => c.estado_cierre === 1)) {
      Swal.fire(
        "Cierre existente",
        "Este desplazamiento ya tiene un cierre activo. Edítalo o inactívalo antes de crear otro.",
        "warning",
      );
      return;
    }

    const payload = this.construirPayload();
    this.guardando = true;

    const obs =
      this.modoEdicion && this.idCierreEditando
        ? this.cierresService.update(this.idCierreEditando, payload)
        : this.cierresService.create({ ...payload, estado_cierre: 1 });

    obs.subscribe({
      next: () => {
        this.guardando = false;
        Swal.fire("Éxito", "Cierre guardado correctamente.", "success");
        this.cancelarEdicion();
        this.cargarCierres();
        this.activeTab = 2;
      },
      error: (error) => {
        this.guardando = false;
        Swal.fire(
          "Error",
          error?.error?.message || error?.error?.error || "No se pudo guardar el cierre.",
          "error",
        );
      },
    });
  }

  editar(cierre: CierresDesplazamientos): void {
    if (Number(cierre.estado_cierre) !== 1) {
      Swal.fire("No se puede editar", "Solo se pueden editar cierres activos.", "warning");
      return;
    }

    if (!cierre.id) return;

    this.cierresService.getById(cierre.id).subscribe({
      next: (respuesta: any) => {
        const detalle = respuesta?.data ?? respuesta;
        this.modoEdicion = true;
        this.idCierreEditando = detalle.id ?? cierre.id;
        this.cierreForm.patchValue({
          fecha_cierre: detalle.fecha_cierre?.substring(0, 10),
          hora_llegada_real: detalle.hora_llegada_real?.substring(0, 5) ?? "",
          kilometraje_final: detalle.kilometraje_final ?? null,
          cumplimiento: detalle.cumplimiento ?? null,
          observaciones: detalle.observaciones ?? "",
        });
        this.activeTab = 1;
      },
      error: () => Swal.fire("Error", "No se pudo cargar el cierre para editar.", "error"),
    });
  }

  cancelarEdicion(): void {
    this.modoEdicion = false;
    this.idCierreEditando = null;
    this.cierreForm.reset({
      fecha_cierre: "",
      hora_llegada_real: "",
      kilometraje_final: null,
      cumplimiento: null,
      observaciones: "",
    });
    if (!this.desplazamientoActivo) this.activeTab = 2;
  }

  // ============ Activar / Inactivar ============
  inactivar(cierre: CierresDesplazamientos): void {
    Swal.fire({
      title: "¿Inactivar cierre?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, inactivar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed || !cierre.id) return;
      this.cierresService.inactivar(cierre.id).subscribe({
        next: () => {
          Swal.fire("Éxito", "Cierre inactivado.", "success");
          this.cargarCierres();
        },
        error: () => Swal.fire("Error", "No se pudo inactivar el cierre.", "error"),
      });
    });
  }

  activar(cierre: CierresDesplazamientos): void {
    Swal.fire({
      title: "¿Activar cierre?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, activar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed || !cierre.id) return;
      this.cierresService.activar(cierre.id).subscribe({
        next: () => {
          Swal.fire("Éxito", "Cierre activado.", "success");
          this.cargarCierres();
        },
        error: () => Swal.fire("Error", "No se pudo activar el cierre.", "error"),
      });
    });
  }

  // ============ Navegación ============
  volver(): void {
    this.location.back();
  }

  // ============ Exportar ============
  exportarExcel(tableId: string, nameFile: string): void {
    Utils.exportVisibleTableToExcel(tableId, nameFile);
  }

  exportToPDF(tableId: string, nameFile: string, titleFile: string): void {
    Utils.exportToPDF(tableId, nameFile, titleFile);
  }
}